import React, { useMemo, useState } from 'react';
import { useFrame } from '@react-three/fiber';
import { Box, Sphere, Cylinder, Text, Billboard } from '@react-three/drei';
import { Color } from 'three';
import { SceneObject, SensorData } from '../../../types';
import { getColorFromValue } from '../../../utils';

interface DeviceObjectProps {
  object: SceneObject;
  isSelected: boolean;
  deviceData?: {
    sensors: SensorData[];
    heatmap: any[];
  };
  visualizationMode: string;
  showDataLabels: boolean;
  showHeatmap: boolean;
}

export const DeviceObject: React.FC<DeviceObjectProps> = ({
  object,
  isSelected,
  deviceData,
  showDataLabels,
  showHeatmap,
}) => {
  const [hovered, setHovered] = useState(false);
  const [animationTime, setAnimationTime] = useState(0);

  useFrame((state, delta) => {
    setAnimationTime((time) => time + delta);
  });

  // Get device-specific properties
  const deviceType = object.userData?.deviceType || 'sensor';

  // Get current sensor value for visualization
  const currentValue = useMemo(() => {
    if (!deviceData?.sensors?.length) return 0;
    return deviceData.sensors[0]?.value || 0;
  }, [deviceData]);

  // Color based on sensor data (for heatmap mode)
  const heatmapColor = useMemo(() => {
    if (!showHeatmap || !deviceData?.sensors?.length) {
      return new Color('#666666');
    }

    const sensor = deviceData.sensors[0];
    const value = sensor.value;
    const minValue = sensor.history?.reduce((min, h) => Math.min(min, h.value), value) || 0;
    const maxValue = sensor.history?.reduce((max, h) => Math.max(max, h.value), value) || 100;

    return getColorFromValue(value, minValue, maxValue, 'temperature');
  }, [showHeatmap, deviceData]);

  // Device color
  const deviceColor = useMemo(() => {
    if (showHeatmap) return heatmapColor;
    if (isSelected) return new Color('#00ff88');
    if (hovered) return new Color('#88ff88');
    return new Color('#666666');
  }, [showHeatmap, heatmapColor, isSelected, hovered]);

  // Device geometry based on type
  const renderDeviceGeometry = () => {
    const scale = object.scale || [1, 1, 1];

    switch (deviceType) {
      case 'temperature-sensor':
        return (
          <Cylinder args={[0.3, 0.3, 1.2, 8]} scale={scale}>
            <meshPhongMaterial color={deviceColor} />
          </Cylinder>
        );

      case 'humidity-sensor':
        return (
          <Box args={[0.8, 0.8, 0.4]} scale={scale}>
            <meshPhongMaterial color={deviceColor} />
          </Box>
        );

      case 'motion-sensor':
        return (
          <Sphere args={[0.4, 16, 16]} scale={scale}>
            <meshPhongMaterial color={deviceColor} />
          </Sphere>
        );

      case 'camera':
        return (
          <group>
            <Box args={[0.6, 0.4, 0.8]} scale={scale}>
              <meshPhongMaterial color={deviceColor} />
            </Box>
            <Cylinder args={[0.2, 0.2, 0.6, 8]} rotation={[Math.PI / 2, 0, 0]} position={[0, 0, 0.5]} scale={scale}>
              <meshPhongMaterial color="#333333" />
            </Cylinder>
          </group>
        );

      case 'smart-light':
        return (
          <Cylinder args={[0.3, 0.5, 1.0, 8]} scale={scale}>
            <meshPhongMaterial color={deviceColor} />
          </Cylinder>
        );

      default:
        return (
          <Box args={[0.6, 0.6, 0.6]} scale={scale}>
            <meshPhongMaterial color={deviceColor} />
          </Box>
        );
    }
  };

  // Data label component
  const renderDataLabel = () => {
    if (!showDataLabels || !deviceData?.sensors?.length) return null;

    const sensor = deviceData.sensors[0];
    const displayText = `${sensor.value.toFixed(1)}${sensor.unit}`;

    return (
      <Billboard position={[0, 1.5, 0]} follow={true}>
        <Text fontSize={0.3} color={isSelected ? '#00ff88' : '#ffffff'} anchorX="center" anchorY="middle">
          {displayText}
        </Text>
      </Billboard>
    );
  };

  // Alert indicator
  const renderAlertIndicator = () => {
    if (!deviceData?.sensors?.length) return null;

    const sensor = deviceData.sensors[0];
    const isAlert = sensor.value > 30 || sensor.value < 10;

    if (!isAlert) return null;

    const alertOpacity = Math.sin(animationTime * 4) * 0.3 + 0.7;

    return (
      <Billboard position={[0, 2, 0]} follow={true}>
        <Sphere args={[0.1, 8, 8]}>
          <meshBasicMaterial color="#ff0000" opacity={alertOpacity} />
        </Sphere>
      </Billboard>
    );
  };

  return (
    <group
      position={object.position as [number, number, number]}
      rotation={object.rotation as [number, number, number]}
      onPointerEnter={() => setHovered(true)}
      onPointerLeave={() => setHovered(false)}
    >
      {renderDeviceGeometry()}
      {renderDataLabel()}
      {renderAlertIndicator()}

      {/* Selection outline */}
      {isSelected && (
        <Box args={[1.2, 1.2, 1.2]} scale={object.scale || [1, 1, 1]}>
          <meshBasicMaterial color="#00ff88" opacity={0.5} />
        </Box>
      )}
    </group>
  );
};
